import React, { useEffect, useMemo, useState } from 'react';
import ModalFrame from './modals/ModalFrame';
import SearchableSelect from './Form/SearchableSelect';
import { CLOSE_ENROLLMENT_STATUS_OPTIONS } from '../domain/enrollment/constants';
import {
  filterBatchesForBatchChange,
  pickDefaultSourceEnrollmentId
} from '../domain/enrollment/batchEligibility';
import { formatEnrollmentSelectLabel } from '../domain/enrollment/batchLabels';
import { BATCH_SEARCHABLE_SELECT_PROPS } from '../domain/batch/batchDisplayLabel';
import { enrollmentCommands } from '../domain/enrollment/enrollmentCommands';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

/**
 * Modal: move student to another batch by closing one enrollment and creating a new row.
 * Depends on enrollmentCommands (DIP); eligibility rules live in domain (SRP).
 */
const ChangeBatchModal = ({
  isOpen,
  onClose,
  student,
  batches,
  onSuccess,
  commands = enrollmentCommands
}) => {
  const enrollments = student?.enrollments || [];
  const [fromEnrollmentId, setFromEnrollmentId] = useState('');
  const [toBatchId, setToBatchId] = useState('');
  const [closePreviousStatus, setClosePreviousStatus] = useState('Left in between');
  const [transferNote, setTransferNote] = useState('');
  const [newEnrollmentRemarks, setNewEnrollmentRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !student?.enrollments?.length) return;
    const list = student.enrollments;
    setFromEnrollmentId(pickDefaultSourceEnrollmentId(list));
    setToBatchId('');
    setClosePreviousStatus('Left in between');
    setTransferNote('');
    setNewEnrollmentRemarks('');
    setError('');
  }, [isOpen, student?.id]);

  const eligibleTargetBatches = useMemo(
    () => filterBatchesForBatchChange(batches, enrollments, fromEnrollmentId),
    [batches, enrollments, fromEnrollmentId]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fromEnrollmentId || !toBatchId) {
      setError('Select the enrollment to move from and a target batch.');
      return;
    }
    setSubmitting(true);
    try {
      await commands.changeBatch({
        fromEnrollmentId,
        toBatchId: parseInt(toBatchId, 10),
        closePreviousStatus,
        transferNote: transferNote.trim() || undefined,
        newEnrollmentRemarks: newEnrollmentRemarks.trim() || undefined
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const footerCloseOnly = (
    <div className="flex justify-end mt-4">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
      >
        Close
      </button>
    </div>
  );

  const formFooter = (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="change-batch-form"
        disabled={submitting || eligibleTargetBatches.length === 0}
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? 'Saving…' : 'Change batch'}
      </button>
    </div>
  );

  if (!isOpen || !student) return null;

  return (
    <ModalFrame
      isOpen
      onClose={onClose}
      titleId="change-batch-title"
      title="Change batch"
      description={
        <>
          The current enrollment will be updated with the status you choose and a note. A{' '}
          <strong>new</strong> enrollment is created in the target batch (same or different course).
        </>
      }
      footer={enrollments.length === 0 ? footerCloseOnly : formFooter}
    >
      {enrollments.length === 0 ? (
        <p className="text-sm text-gray-700">This student has no enrollments.</p>
      ) : (
        <form id="change-batch-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Move from enrollment
            </label>
            <SearchableSelect
              value={fromEnrollmentId}
              onChange={(ev) => setFromEnrollmentId(ev.target.value)}
              className="w-full"
              required
              options={enrollments}
              getOptionValue={(en) => en.id}
              getOptionLabel={(en) => formatEnrollmentSelectLabel(en)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New batch</label>
            <SearchableSelect
              value={toBatchId}
              onChange={(ev) => setToBatchId(ev.target.value)}
              className="w-full"
              placeholder="Select target batch…"
              required
              options={eligibleTargetBatches}
              {...BATCH_SEARCHABLE_SELECT_PROPS}
            />
            {eligibleTargetBatches.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                No eligible batches (same batch excluded; batches where this student is already
                enrolled are excluded).
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status for previous enrollment
            </label>
            <SearchableSelect
              value={closePreviousStatus}
              onChange={(ev) => setClosePreviousStatus(ev.target.value)}
              className="w-full"
              required
              options={CLOSE_ENROLLMENT_STATUS_OPTIONS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Optional note (appended to previous enrollment)
            </label>
            <textarea
              value={transferNote}
              onChange={(ev) => setTransferNote(ev.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Reason or context for the transfer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks on new enrollment (optional)
            </label>
            <textarea
              value={newEnrollmentRemarks}
              onChange={(ev) => setNewEnrollmentRemarks(ev.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
              {error}
            </div>
          )}
        </form>
      )}
    </ModalFrame>
  );
};

export default ChangeBatchModal;
